import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { UISliceState, Notification } from '../types';

/**
 * Redux slice for UI state management
 */

const initialState: UISliceState = {
  showHUD: true,
  showMenu: false,
  showPauseMenu: false,
  showUpgradeMenu: false,
  showSettingsMenu: false,
  showObjectives: true,
  showMinimap: true,
  currentMenu: null,
  notifications: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // HUD
    setShowHUD: (state, action: PayloadAction<boolean>) => {
      state.showHUD = action.payload;
    },

    toggleHUD: (state) => {
      state.showHUD = !state.showHUD;
    },

    // Menus
    setShowMenu: (state, action: PayloadAction<boolean>) => {
      state.showMenu = action.payload;
      if (!action.payload) {
        state.currentMenu = null;
      }
    },

    setShowPauseMenu: (state, action: PayloadAction<boolean>) => {
      state.showPauseMenu = action.payload;
      state.currentMenu = action.payload ? 'pause' : null;
    },

    togglePauseMenu: (state) => {
      state.showPauseMenu = !state.showPauseMenu;
      state.currentMenu = state.showPauseMenu ? 'pause' : null;
    },

    setShowUpgradeMenu: (state, action: PayloadAction<boolean>) => {
      state.showUpgradeMenu = action.payload;
      state.currentMenu = action.payload ? 'upgrade' : null;
    },

    toggleUpgradeMenu: (state) => {
      state.showUpgradeMenu = !state.showUpgradeMenu;
      state.currentMenu = state.showUpgradeMenu ? 'upgrade' : null;
    },

    setShowSettingsMenu: (state, action: PayloadAction<boolean>) => {
      state.showSettingsMenu = action.payload;
      state.currentMenu = action.payload ? 'settings' : null;
    },

    toggleSettingsMenu: (state) => {
      state.showSettingsMenu = !state.showSettingsMenu;
      state.currentMenu = state.showSettingsMenu ? 'settings' : null;
    },

    setCurrentMenu: (state, action: PayloadAction<string | null>) => {
      state.currentMenu = action.payload;
      // Update individual menu states
      state.showPauseMenu = action.payload === 'pause';
      state.showUpgradeMenu = action.payload === 'upgrade';
      state.showSettingsMenu = action.payload === 'settings';
      state.showMenu = action.payload !== null;
    },

    closeAllMenus: (state) => {
      state.showMenu = false;
      state.showPauseMenu = false;
      state.showUpgradeMenu = false;
      state.showSettingsMenu = false;
      state.currentMenu = null;
    },

    // HUD elements
    setShowObjectives: (state, action: PayloadAction<boolean>) => {
      state.showObjectives = action.payload;
    },

    toggleObjectives: (state) => {
      state.showObjectives = !state.showObjectives;
    },

    setShowMinimap: (state, action: PayloadAction<boolean>) => {
      state.showMinimap = action.payload;
    },

    toggleMinimap: (state) => {
      state.showMinimap = !state.showMinimap;
    },

    // Notifications
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: `notification-${Date.now()}-${Math.random()}`,
        timestamp: Date.now(),
      };
      state.notifications.push(notification);
    },

    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },

    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Reset UI
    resetUI: () => initialState,
  },
});

export const {
  setShowHUD,
  toggleHUD,
  setShowMenu,
  setShowPauseMenu,
  togglePauseMenu,
  setShowUpgradeMenu,
  toggleUpgradeMenu,
  setShowSettingsMenu,
  toggleSettingsMenu,
  setCurrentMenu,
  closeAllMenus,
  setShowObjectives,
  toggleObjectives,
  setShowMinimap,
  toggleMinimap,
  addNotification,
  removeNotification,
  clearNotifications,
  resetUI,
} = uiSlice.actions;

export default uiSlice.reducer;
